import {
    defaultTaskNotificationSubject,
    TaskTypeId,
    WorkflowActionId,
} from "@sp-crm/core";
import {
    activityRecordTypeForEntityKind,
    recordTypeActivityChoices,
    useActivityTypeOptions,
} from "components/tasks/activity-type-options";
import { TopAlignedRadio } from "components/ui/radio";
import { Select } from "components/ui/select";
import { produce } from "immer";
import React, { useCallback } from "react";
import { entityTypeNoun } from "./manage-workflows-helper";
import { RecurrenceEditor } from "./recurrence-editor";
import { EditableWorkflow } from "./types";

interface TaskActionEditorProps {
    workflow: EditableWorkflow;
    actionId: WorkflowActionId;
    onChange: (workflow: EditableWorkflow) => void;
}

type EditableTaskAction = Extract<
    EditableWorkflow["actions"][number],
    { __typename: "WorkflowActionTaskEntity" }
>;

export const taskActionById = (
    workflow: EditableWorkflow,
    actionId: WorkflowActionId,
): EditableTaskAction | null => {
    const action = workflow.actions.find(candidate => candidate.id === actionId);
    return action?.__typename === "WorkflowActionTaskEntity" ? action : null;
};

export const TaskConfigurationEditor: React.FC<TaskActionEditorProps> = props => {
    const { workflow, actionId, onChange } = props;

    const editable = !!workflow.regionVisibility?.editable;

    const activityTypes = useActivityTypeOptions();

    const handleTypeChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newWorkflow = produce(workflow, draft => {
                const action = taskActionById(draft, actionId);

                if (action) {
                    action.taskTypeId = e.target.value || null;
                }
            });

            onChange(newWorkflow);
        },
        [workflow, actionId, onChange],
    );

    const action = taskActionById(workflow, actionId);
    if (!action) {
        return null;
    }

    const offeredTypes = recordTypeActivityChoices(
        activityTypes,
        activityRecordTypeForEntityKind(workflow.attachTo),
        (action.taskTypeId as TaskTypeId | null) ?? null,
    );

    return (
        <div className="space-y-4">
            <div>
                <Select
                    disabled={!editable}
                    label="Activity type"
                    value={action.taskTypeId || ""}
                    onChange={handleTypeChange}>
                    <option value=""></option>
                    {offeredTypes.map(t => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </Select>
                <div className="ml-6 text-gray-500 group-hover:text-gray-900 text-sm">
                    Tasks created by this workflow will use this type.
                </div>
            </div>

            <RecurrenceEditor
                workflow={workflow}
                actionId={actionId}
                onChange={onChange}
                editable={editable}
            />
        </div>
    );
};

export const TaskNotificationEditor: React.FC<TaskActionEditorProps> = props => {
    const { workflow, actionId, onChange } = props;

    const editable = !!workflow.regionVisibility?.editable;
    const noun = entityTypeNoun(workflow.attachTo);

    const action = taskActionById(workflow, actionId);

    const handleNotificationModeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const mode = e.target.value as "manual" | "automated";

            const newWorkflow = produce(workflow, draft => {
                const action = taskActionById(draft, actionId);

                if (action) {
                    action.notifyOwner = mode === "automated";
                }
            });

            onChange(newWorkflow);
        },
        [workflow, actionId, onChange],
    );

    if (!action) {
        return null;
    }

    return (
        <div className="space-y-3">
            <p className="text-sm text-gray-500">
                For task owners with notifications enabled, choose how to notify them when
                this workflow creates a task.
            </p>

            <div className="space-y-2">
                <TopAlignedRadio
                    disabled={!editable}
                    value="manual"
                    checked={!action.notifyOwner}
                    onChange={handleNotificationModeChange}
                    label={
                        <div>
                            <div>
                                Notify when not {noun} owner:{" "}
                                <span className="italic">
                                    [Jane Doe] assigned a task to you
                                </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                                {`Acts like a manual task assignment from the ${noun} owner. Avoids redundant notifications when the task and ${noun} owner are the same.`}
                            </div>
                        </div>
                    }
                />
                <TopAlignedRadio
                    disabled={!editable}
                    value="automated"
                    checked={action.notifyOwner}
                    onChange={handleNotificationModeChange}
                    label={
                        <div>
                            <div>
                                Always notify with subject:{" "}
                                <span className="italic">
                                    {action.notification?.subject ||
                                        defaultTaskNotificationSubject}
                                </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                                <p>
                                    {`Will be sent even if the task owner is the ${noun} owner. Best for critical or delayed tasks to act as an additional reminder.`}
                                </p>
                            </div>
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export const TaskActionEditor: React.FC<TaskActionEditorProps> = props => {
    const { workflow, actionId, onChange } = props;

    return (
        <div className="space-y-4">
            <TaskConfigurationEditor
                workflow={workflow}
                actionId={actionId}
                onChange={onChange}
            />
            <div className="space-y-3">
                <h3 className="font-medium text-gray-900">
                    Task assignment notification
                </h3>
                <TaskNotificationEditor
                    workflow={workflow}
                    actionId={actionId}
                    onChange={onChange}
                />
            </div>
        </div>
    );
};
